/**
 * Stage 73.20r — ESS KYC Confirmation page.
 *
 * Employee sees the masked snapshot of their master data. Two
 * exclusive actions: Confirm everything is correct, OR pick specific
 * fields to request a correction (with optional proof upload + note).
 * They cannot edit the master directly — only HR-approved corrections
 * patch the master.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ShieldCheck, CheckCircle2, AlertTriangle, Loader2, FileText,
  Upload, X, History,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ESSKycConfirmation() {
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(null); // confirmation_id
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('view'); // view | correct
  const [correctionMap, setCorrectionMap] = useState({}); // {field_key: {new_value, employee_note, proof_file_id, proof_filename}}
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ess/kyc/pending`, { withCredentials: true });
      setPending(res.data || []);
      if ((res.data || []).length > 0 && !selected) setSelected(res.data[0].id);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [selected]);

  useEffect(() => { loadPending(); }, [loadPending]);
  const loadDetail = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ess/kyc/${id}`, { withCredentials: true });
      setDetail(res.data);
      setCorrectionMap({});
      setMode('view');
    } catch (e) {
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (selected) loadDetail(selected); }, [selected, loadDetail]);



  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/ess/kyc/history`, { withCredentials: true });
      setHistory(res.data);
      setShowHistory(true);
    } catch (e) {
      toast.error('History load failed');
    }
  };

  const confirmAll = async () => {
    if (!detail) return;
    if (!window.confirm('I confirm that all the details shown are correct.')) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/ess/kyc/${detail.id}/confirm`, { remarks: '' }, { withCredentials: true });
      toast.success('Confirmation submitted — thank you!');
      await loadPending();
      setSelected(null);
      setDetail(null);
    } catch (e) {
      toast.error('Confirm failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSubmitting(false);
  };

  const uploadProof = async (fieldKey, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/ess/kyc/upload-proof`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCorrectionMap(m => ({
        ...m,
        [fieldKey]: { ...(m[fieldKey] || {}), proof_file_id: res.data.file_id, proof_filename: res.data.filename },
      }));
      toast.success('Proof uploaded');
    } catch (e) {
      toast.error('Upload failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const submitCorrections = async () => {
    if (!detail) return;
    const items = Object.entries(correctionMap)
      .filter(([_, v]) => v && (v.new_value !== undefined && v.new_value !== null && String(v.new_value).trim() !== ''))
      .map(([field, v]) => ({
        field,
        new_value: v.new_value,
        proof_file_id: v.proof_file_id || null,
        employee_note: v.employee_note || '',
      }));
    if (items.length === 0) {
      toast.error('Pick at least one field and type the corrected value.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/ess/kyc/${detail.id}/request-correction`, { items }, { withCredentials: true });
      toast.success(`${items.length} correction(s) submitted to HR for approval.`);
      await loadPending();
      setSelected(null);
      setDetail(null);
    } catch (e) {
      toast.error('Submit failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSubmitting(false);
  };

  // Group fields by section.
  const grouped = useMemo(() => {
    if (!detail) return {};
    const out = {};
    for (const f of detail.fields) {
      if (!out[f.section]) out[f.section] = [];
      out[f.section].push(f);
    }
    return out;
  }, [detail]);

  return (
    <MainLayout>
      <div className="p-6 space-y-6" data-testid="ess-kyc-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-blue-600" />
              KYC / Data Confirmation
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Verify your master details. You can <b>Confirm</b> as-is, or pick fields to <b>Request Correction</b> — HR reviews before any change is applied.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadHistory} data-testid="ess-kyc-history-btn">
            <History className="w-3 h-3 mr-1" /> My History
          </Button>
        </div>

        {pending.length === 0 ? (
          <Card className="p-8 text-center" data-testid="ess-kyc-no-pending">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-2" />
            <p className="text-base font-semibold text-slate-800">All caught up</p>
            <p className="text-sm text-slate-500">You have no pending KYC confirmations.</p>
          </Card>
        ) : (
          <>
            {/* Pending list (if multiple) */}
            {pending.length > 1 && (
              <Card className="p-3">
                <p className="text-xs text-slate-600 mb-2">You have {pending.length} pending confirmation{pending.length > 1 ? 's' : ''}:</p>
                <div className="flex flex-wrap gap-2">
                  {pending.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setSelected(p.id)}
                      className={`px-3 py-1 rounded-full text-xs border ${selected === p.id ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-300 text-slate-700'}`}
                      data-testid={`ess-kyc-cycle-${p.id}`}
                    >
                      {p.cycle_name} <span className="text-[10px] opacity-70">· due {p.due_date}</span>
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {loading || !detail ? (
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            ) : (
              <>
                {/* Rejection Banner for Rejected - Action Required */}
                {detail.status === 'rejected_action_required' && (
                  <Card className="p-4 border-red-300 bg-red-50" data-testid="kyc-rejection-banner">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                      <div className="flex-1 text-xs text-red-900 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-sm text-red-700 uppercase tracking-wide">Status: Rejected — Action Required</span>
                          {detail.rejected_at && <span className="text-[11px] text-red-600 font-mono">Date: {detail.rejected_at.slice(0, 10)}</span>}
                        </div>
                        <p><b>HR Rejection Reason:</b> {detail.hr_rejection_reason || 'Re-verification required for submitted documents/values.'}</p>
                        <p><b>Rejected By:</b> {detail.rejected_by_name || 'HR Admin'}</p>
                        <p className="text-[11px] text-red-700 italic pt-1 border-t border-red-200">
                          Please edit your rejected fields, upload corrected documents/proofs, and click <b>Resubmit to HR</b> below. Do not create a new request.
                        </p>
                      </div>
                    </div>
                  </Card>
                )}

                <Card className="p-4 border-blue-200 bg-blue-50/30">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800">{detail.cycle_name}</h3>
                      <p className="text-[11px] text-slate-600">
                        Due by <b>{detail.due_date}</b>. Status: {' '}
                        <span className={`font-semibold capitalize px-2 py-0.5 rounded text-[11px] ${
                          detail.status === 'rejected_action_required' ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {detail.status === 'rejected_action_required' ? 'Rejected — Action Required' : detail.status}
                        </span>
                      </p>
                    </div>
                    {mode === 'view' ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => setMode('correct')}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid="ess-kyc-request-mode"
                        >
                          {detail.status === 'rejected_action_required' ? 'Edit & Resubmit' : 'Request Correction'}
                        </Button>
                        {detail.status !== 'rejected_action_required' && (
                          <Button
                            size="sm"
                            onClick={confirmAll}
                            disabled={submitting}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="ess-kyc-confirm-btn"
                          >
                            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3 mr-1" /> I Confirm</>}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setMode('view'); setCorrectionMap({}); }}>Cancel</Button>
                        <Button
                          size="sm"
                          onClick={submitCorrections}
                          disabled={submitting || Object.keys(correctionMap).length === 0}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="ess-kyc-submit-corrections"
                        >
                          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : (detail.status === 'rejected_action_required' ? 'Resubmit to HR' : 'Submit to HR')}
                        </Button>
                      </div>
                    )}
                  </div>
                  {mode === 'correct' && (
                    <p className="text-[11px] mt-2 text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
                      <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      Tick the box next to fields you want to correct. Enter the new value, optionally attach proof, and add a note. HR approval is required before the master is updated.
                    </p>
                  )}
                </Card>

                {/* Sections */}
                {Object.entries(grouped).map(([section, fields]) => (
                  <Card key={section} className="p-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{section}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {fields.map(f => (
                        <FieldRow
                          key={f.key}
                          field={f}
                          mode={mode}
                          correction={correctionMap[f.key]}
                          onToggle={(on) => {
                            setCorrectionMap(m => {
                              const copy = { ...m };
                              if (on) copy[f.key] = { new_value: '', employee_note: '' };
                              else delete copy[f.key];
                              return copy;
                            });
                          }}
                          onChange={(patch) => setCorrectionMap(m => ({ ...m, [f.key]: { ...(m[f.key] || {}), ...patch } }))}
                          onUploadProof={(file) => uploadProof(f.key, file)}
                        />
                      ))}
                    </div>
                  </Card>
                ))}

                {/* Audit Trail Timeline */}
                {detail.audit_log && detail.audit_log.length > 0 && (
                  <Card className="p-4 border-slate-200 bg-slate-50/50" data-testid="kyc-audit-trail">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-3 flex items-center gap-1.5">
                      <History className="w-3.5 h-3.5 text-blue-600" /> Complete Audit Trail & Workflow History
                    </h4>
                    <div className="space-y-2">
                      {detail.audit_log.map((log, idx) => (
                        <div key={idx} className="border-l-2 border-blue-400 pl-3 py-1 text-xs space-y-0.5">
                          <div className="flex items-center justify-between text-slate-700 font-medium">
                            <span className="capitalize text-slate-900">{log.action ? log.action.replace('_', ' ') : 'Action Logged'}</span>
                            <span className="text-[10px] text-slate-400 font-mono">{(log.timestamp || '').slice(0, 19).replace('T', ' ')}</span>
                          </div>
                          <p className="text-slate-600 text-[11px]">
                            By: <b>{log.actor_name || 'System User'}</b> ({log.actor_role || 'User'})
                          </p>
                          {log.field && (
                            <p className="text-slate-600 text-[11px]">
                              Field: <b>{log.field}</b> | Old: <span className="font-mono text-rose-700">{String(log.old_value ?? '—')}</span> → New: <span className="font-mono text-emerald-700">{String(log.new_value ?? '—')}</span>
                            </p>
                          )}
                          {log.remarks && <p className="text-slate-500 italic text-[11px]">Remarks: "{log.remarks}"</p>}
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </>
            )}
          </>
        )}

        {/* History modal */}
        {showHistory && history && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowHistory(false)}>
            <div className="bg-white rounded-xl p-5 w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">My KYC History</h3>
                <button onClick={() => setShowHistory(false)}><X className="w-4 h-4" /></button>
              </div>
              <Card className="p-3 mb-3">
                <h4 className="text-xs font-semibold mb-2">Past Confirmations ({history.confirmations.length})</h4>
                {history.confirmations.length === 0 ? <p className="text-[11px] text-slate-500 italic">None yet.</p> : (
                  <ul className="text-xs space-y-1">
                    {history.confirmations.slice(0, 10).map(c => (
                      <li key={c.id} className="border-t border-slate-100 pt-1">
                        <b>{c.cycle_name}</b> · <span className="capitalize">{c.status}</span>
                        {c.confirmed_at && <span className="text-[10px] text-slate-500"> · confirmed {c.confirmed_at.slice(0, 10)}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
              <Card className="p-3">
                <h4 className="text-xs font-semibold mb-2">Correction Requests ({history.corrections.length})</h4>
                {history.corrections.length === 0 ? <p className="text-[11px] text-slate-500 italic">No corrections submitted.</p> : (
                  <ul className="text-xs space-y-1">
                    {history.corrections.slice(0, 20).map(c => (
                      <li key={c.id} className="border-t border-slate-100 pt-1">
                        <b>{c.field}</b>: "{String(c.old_value ?? '—') || '—'}" → "{String(c.new_value ?? '—') || '—'}"
                        <span className={`ml-1 px-1.5 py-0.5 rounded text-[9px] uppercase ${c.hr_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : c.hr_status === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                          {c.hr_status}
                        </span>
                        {c.hr_remarks && <div className="text-[10px] italic text-slate-500">"{c.hr_remarks}"</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

function FieldRow({ field, mode, correction, onToggle, onChange, onUploadProof }) {
  const checked = !!correction;
  const isCorrect = mode === 'correct';
  return (
    <div className={`rounded border p-2 ${checked ? 'border-blue-300 bg-blue-50/40' : 'border-slate-200 bg-white'}`} data-testid={`ess-kyc-field-${field.key}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-slate-500">{field.label}</div>
        {isCorrect && field.editable && (
          <input
            type="checkbox"
            checked={checked}
            onChange={e => onToggle(e.target.checked)}
            className="cursor-pointer"
            data-testid={`ess-kyc-field-toggle-${field.key}`}
          />
        )}
      </div>
      <div className="text-sm font-medium text-slate-800 mt-0.5">
        {field.display_value || <span className="italic text-slate-400">(empty)</span>}
        {field.mask && <span className="ml-1 text-[10px] text-slate-400">masked</span>}
      </div>
      {isCorrect && checked && field.editable && (
        <div className="mt-2 space-y-1.5">
          {field.multiline ? (
            <textarea
              rows={2}
              placeholder="Corrected value"
              value={correction.new_value || ''}
              onChange={e => onChange({ new_value: e.target.value })}
              className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
              data-testid={`ess-kyc-field-new-${field.key}`}
            />
          ) : (
            <input
              type="text"
              placeholder="Corrected value"
              value={correction.new_value || ''}
              onChange={e => onChange({ new_value: e.target.value })}
              className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
              data-testid={`ess-kyc-field-new-${field.key}`}
            />
          )}
          <input
            type="text"
            placeholder="Note (e.g. moved to new address last month)"
            value={correction.employee_note || ''}
            onChange={e => onChange({ employee_note: e.target.value })}
            className="w-full border border-slate-300 rounded px-2 py-1 text-[11px]"
          />
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 cursor-pointer text-[10px] text-blue-700">
              <Upload className="w-3 h-3" />
              {correction.proof_filename || 'Attach proof (PDF/image)'}
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                className="hidden"
                onChange={e => e.target.files?.[0] && onUploadProof(e.target.files[0])}
                data-testid={`ess-kyc-field-proof-${field.key}`}
              />
            </label>
            {correction.proof_file_id && <FileText className="w-3 h-3 text-emerald-600" />}
          </div>
        </div>
      )}
      {isCorrect && !field.editable && (
        <p className="text-[10px] text-slate-400 italic mt-1">Read-only — corrections must be raised through HR transfer flow.</p>
      )}
    </div>
  );
}
